import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { ConsultButton, WhatsAppLink, CallLink } from "./CtaButtons";
import { useLanguage } from "@/i18n/LanguageContext";
import { track } from "@/lib/api";

const NAV = [
  ["nav.home", "/"],
  ["nav.about", "/about"],
  ["nav.services", "/services"],
  ["nav.projects", "/projects"],
  ["nav.process", "/process"],
  ["nav.estimate", "/estimate"],
  ["nav.contact", "/contact"],
];

export const LangSwitch = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center gap-1" data-testid="language-switcher">
      {["en", "kn"].map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`lang-${l}-button`}
          aria-pressed={lang === l}
          onClick={() => { setLang(l); track("language_select", { lang: l }); }}
          className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
            lang === l ? "bg-gold text-charcoal" : "text-white/60 hover:text-white"
          }`}
        >
          {l === "en" ? "EN" : "ಕನ್ನಡ"}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 bg-charcoal/85 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 ${
          scrolled ? "border-b border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.35)]" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8">
          <Link to="/" aria-label="D & N Constructions home" data-testid="header-logo-link">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV.map(([key, to]) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${key.split(".")[1]}-link`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                    isActive ? "text-gold" : "text-white/70 hover:text-white"
                  }`
                }
              >
                {t(key)}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-5 lg:flex">
            <LangSwitch />
            <ConsultButton testid="header-consult-button" className="px-5 py-2.5 text-xs" />
          </div>
          <div className="flex items-center gap-3 lg:hidden">
            <LangSwitch />
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-full border border-white/15 p-2.5 text-offwhite"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-ink/98 pt-24 backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-col gap-2 px-8" aria-label="Mobile">
              {NAV.map(([key, to], i) => (
                <motion.div
                  key={to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                >
                  <NavLink
                    to={to}
                    data-testid={`mobile-nav-${key.split(".")[1]}-link`}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-display text-3xl ${
                        isActive ? "text-gold" : "text-offwhite"
                      }`
                    }
                  >
                    {t(key)}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-4 px-8">
              <ConsultButton testid="mobile-menu-consult-button" className="w-full" />
              <div className="flex items-center justify-between">
                <CallLink testid="mobile-menu-call-link" className="text-offwhite" />
                <WhatsAppLink testid="mobile-menu-whatsapp-link" className="px-5 py-2.5 text-xs" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
